// components/RoomLobby.jsx: pre-room lobby wizard.
//
// A 3-step flow (see lobby-flow.js):
//   'choose' → a clean landing screen with just Create Room / Join Room
//              plus a plainly-visible "How to play" link.
//   'create' → "Your identity" (name + color) then the Create Room confirm.
//   'join'   → "Your identity" then the 6-char room-code entry + Join.
// Identity is collected AFTER the user picks create vs join so the landing
// screen stays uncluttered. On successful create/join we transition to the
// in-room view (RoomCode + TeamColumns).

import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { getOrCreateIdentity } from '../identity/identity.js';
import { lastGameText } from '../hooks/useRoomWs.js';
import { IdentityPanel } from './IdentityPanel.jsx';
import { HowToPlayTrigger } from '../nav/HowToPlayTrigger.jsx';
import { initialLobbyStep, stepShowsIdentity } from './lobby-flow.js';
import { roomPageConnectErrorText } from '../views/networked-flow.js';
import { RoomCode } from './RoomCode.jsx';
import { roomNextText } from './room-next.js';
import { TeamColumns } from './TeamColumns.jsx';
import { RoomSettings } from './RoomSettings.jsx';
import { StartGameButton } from './StartGameButton.jsx';
import { loadLastRoom, subscribeLastRoom } from '../nav/last-room.js';
import { countOtherNotReady } from './start-game-logic.js';
import { GameStartingPlaceholder } from './GameStartingPlaceholder.jsx';
import { NetworkedGameView } from '../views/NetworkedGameView.jsx';
import { ReadyToggle } from './ReadyToggle.jsx';
import { removalMessage, REMOVAL_BUTTON_LABEL } from './removal-copy.js';
import '../styles/room-lobby.css';

const ROOM_CODE_LENGTH = 6;
// Mirrors server/src/room/code-gen.js, excludes confusables 0/O/1/I/L.
const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Browser-side mirror of the server's nanoid alphabet. Lowercase input
 * is auto-uppercased; chars outside the alphabet (including 0/O/1/I/L
 * confusables) are dropped on the way in.
 */
function sanitizeCodeInput(raw) {
  const upper = (raw || '').toUpperCase();
  let out = '';
  for (const ch of upper) {
    if (ROOM_CODE_ALPHABET.includes(ch)) out += ch;
    if (out.length === ROOM_CODE_LENGTH) break;
  }
  return out;
}

/**
 * Narrow-phone flag (<700 px). Phones render the fixed bottom action bar
 * (Batch D D6) instead of the in-flow Ready/Start row, so exactly one
 * Ready toggle and one Start button ever exist in the DOM.
 */
function useIsNarrow(breakpoint = 700) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);
  return narrow;
}

/**
 * @param {{
 *   ws: ReturnType<typeof import('../hooks/useRoomWs.js').useRoomWs>,
 *   onStepChange?: (step: import('./lobby-flow.js').LobbyStep) => void,
 * }} props
 */
export function RoomLobby({ ws, onStepChange }) {
  const [identityName, setIdentityName] = useState('');
  // The wizard step. A ?room=CODE deep-link opens straight on 'join' with
  // the code prefilled (the hook also fires a one-shot auto-join in the
  // background). Lazy initializers read the URL-derived code once, on mount.
  const [step, setStep] = useState(() => initialLobbyStep(ws.initialJoinCode));
  const [codeDraft, setCodeDraft] = useState(() => sanitizeCodeInput(ws.initialJoinCode || ''));

  // B3b F7: the room code joined in the last 2 hours, if any, so the
  // home page can offer a way back after a closed tab. Re-read when this
  // tab or another tab changes it; a missing/unreadable store simply
  // hides the button.
  const [lastRoomCode, setLastRoomCode] = useState(() => loadLastRoom());
  useEffect(() => subscribeLastRoom(window, () => setLastRoomCode(loadLastRoom())), []);

  // Seed the Create button's name gate from the persisted identity on mount.
  // While the identity step is open, IdentityPanel's onChange keeps it fresh.
  useEffect(() => {
    setIdentityName(getOrCreateIdentity().display_name || '');
  }, []);

  // Report the active step up to RoomView so it can hide the (tall) home
  // hero on the create/join steps, that's what keeps the identity panel +
  // join form fully visible without scrolling.
  useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);

  // When the user leaves (or is removed from) a room, snap back to the clean
  // choose screen, otherwise the stale 'create'/'join' step would linger.
  const wasInRoom = useRef(false);
  const inRoomNow = ws.status === 'in_room' && !!ws.roomCode;
  useEffect(() => {
    if (inRoomNow) {
      wasInRoom.current = true;
    } else if (wasInRoom.current) {
      wasInRoom.current = false;
      setStep('choose');
    }
  }, [inRoomNow]);

  const handleIdentityChange = useCallback((id) => {
    setIdentityName((id && id.display_name) || '');
  }, []);

  const goStep = useCallback((next) => {
    if (ws.error) ws.clearError();
    setStep(next);
  }, [ws]);

  // Batch D D6: phones render a fixed bottom action bar (Ready + Start).
  // Exactly one Ready/Start pair ever exists: the bar on narrow screens,
  // an in-flow row everywhere else.
  const narrow = useIsNarrow(700);
  /** @type {{ current: HTMLDivElement | null }} */
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(0);
  // Reserve page space equal to the bar's MEASURED height (never a
  // hard-coded px value) so nothing, including "Leave room", hides under
  // it. ResizeObserver re-measures when the bar's content changes (ready
  // label flips, start reason wraps) without a window resize.
  useEffect(() => {
    if (!narrow) {
      if (barHeight !== 0) setBarHeight(0);
      return;
    }
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      setBarHeight((prev) => (prev === h ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });

  const handleCreate = useCallback(() => {
    ws.createRoom();
  }, [ws]);

  const handleCodeInput = useCallback((e) => {
    const v = sanitizeCodeInput(/** @type {HTMLInputElement} */ (e.target).value);
    setCodeDraft(v);
    if (ws.error) ws.clearError();
  }, [ws]);

  const handleJoinSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    if (codeDraft.length !== ROOM_CODE_LENGTH) return;
    ws.joinRoom(codeDraft);
  }, [codeDraft, ws]);

  // The persisted identity always carries a non-empty display_name (the
  // panel falls back to a Guest_XXXX default), so this gate is a safety net.
  const nameMissing = identityName.trim().length === 0;

  if (ws.removed) {
    return (
      <section class="room-lobby removed" data-testid="room-lobby" data-room-state="removed">
        <div class="room-removed-banner" role="alert" data-testid="room-removed-banner">
          {removalMessage(ws.removed.reason)}
        </div>
        <button
          type="button"
          class="room-cta join"
          data-testid="room-removed-dismiss"
          onClick={ws.dismissRemoval}
        >
          {REMOVAL_BUTTON_LABEL}
        </button>
      </section>
    );
  }

  if (ws.status === 'in_room' && ws.roomCode) {
    if (ws.phase === 'in_game') {
      // M4-11: mount the networked game view once the server has sent
      // GAME_STARTING. The view consumes game events through the shared
      // WS service; useGameWs subscribes via _subscribeForGame.
      return (
        <section class="room-lobby in-game" data-testid="room-lobby" data-room-state="in_game" data-phase={ws.phase}>
          <NetworkedGameView
            service={ws.service}
            mySeatIndex={ws.selfSeatIndex}
            roomOccupants={ws.occupants}
            onLeave={ws.leaveRoom}
            serviceError={ws.error}
          />
        </section>
      );
    }
    if (ws.phase === 'starting') {
      // Pre-init placeholder, kept for older paths that still emit a
      // `starting` phase. M4-9 jumps straight to in_game.
      return (
        <section class="room-lobby in-room" data-testid="room-lobby" data-room-state="in_room" data-phase={ws.phase}>
          <GameStartingPlaceholder
            occupantCount={ws.occupants.length}
            botFill={ws.settings.bot_fill}
            onReturn={ws.leaveRoom}
          />
        </section>
      );
    }
    // M3-14: when every occupant has marked themselves ready, surface
    // a pulse class on Start Game to draw the host's attention. Bots
    // aren't represented as occupants in the lobby yet (they spawn at
    // GAME_STARTING), so "all ready" means every present occupant.
    const allReady = ws.occupants.length > 0 && ws.occupants.every((o) => o.ready);
    const connectError = roomPageConnectErrorText({ status: ws.status, roomCode: ws.roomCode, error: ws.error });
    // B3b F3: how many present occupants are not ready yet; the Start
    // button renders the plain note from this live count.
    const notReadyCount = countOtherNotReady(ws.occupants);
    const self = ws.occupants.find((o) => o.is_you);
    const selfReady = !!self?.ready;
    const selfOccupantId = self?.occupant_id ?? null;
    const hostName = ws.occupants.find((o) => o.is_host)?.display_name || 'the host';
    const readyToggle = (
      <ReadyToggle
        ready={selfReady}
        phase={ws.phase}
        onToggle={ws.setReady}
      />
    );
    const startButton = (
      <StartGameButton
        isHost={ws.isHost}
        phase={ws.phase}
        occupantCount={ws.occupants.length}
        botFill={ws.settings.bot_fill}
        allReady={allReady}
        notReadyCount={notReadyCount}
        onStart={ws.startGame}
      />
    );
    return (
      <section
        class="room-lobby in-room room-page"
        data-testid="room-lobby"
        data-room-state="in_room"
        data-phase={ws.phase}
        data-all-ready={allReady ? 'true' : 'false'}
        data-narrow={narrow ? 'true' : 'false'}
        style={narrow && barHeight > 0 ? { paddingBottom: `${barHeight + 16}px` } : undefined}
      >
        <div class="room-next" role="status" data-testid="room-next">
          <strong>Next:</strong>{roomNextText({
            isHost: ws.isHost,
            occupantCount: ws.occupants.length,
            botFill: ws.settings.bot_fill,
            hostName,
          }).slice(5)}
        </div>
        <RoomCode code={ws.roomCode} />
        {lastGameText(ws.lastGame) && <div class="room-last-game" role="status" data-testid="last-game-banner">{lastGameText(ws.lastGame)}</div>}
        {/* The browser could not even make the socket (WS_CONSTRUCT_FAILED):
            a Reconnect tap fails the same way, so the room page shows the
            reload message instead of leaving the player with a dead banner. */}
        {connectError && (
          <div class="room-error" role="alert" data-testid="room-construct-error">
            {connectError}
          </div>
        )}
        {/* Batch D room page: one roster (pool above two team columns,
            partners across) beside the settings panel on desktop, stacked
            on phones. Join buttons send SET_TEAM (disabled when the team
            is full or you're already on it). */}
        <div class="room-page-grid">
          <TeamColumns
            occupants={ws.occupants}
            myOccupantId={selfOccupantId}
            onJoinTeam={ws.setTeam}
            viewerIsHost={ws.isHost}
            onKick={ws.kickOccupant}
            botFill={ws.settings.bot_fill}
          />
          <RoomSettings
            isHost={ws.isHost}
            settings={ws.settings}
            onUpdate={ws.updateSettings}
            hostName={hostName}
          />
          {!narrow && (
            <div class="room-page-actions" data-testid="room-page-actions">
              {readyToggle}
              {startButton}
            </div>
          )}
        </div>
        <button
          type="button"
          class="room-leave"
          data-testid="room-leave"
          onClick={ws.leaveRoom}
        >
          Leave room
        </button>
        {narrow && (
          <div
            class="room-actionbar"
            data-testid="room-actionbar"
            data-bar-height={barHeight > 0 ? String(barHeight) : undefined}
            ref={barRef}
            role="toolbar"
            aria-label="Room actions"
          >
            {readyToggle}
            {startButton}
          </div>
        )}
      </section>
    );
  }

  // ── Pre-room wizard ──────────────────────────────────────────────────
  // Step 1 ('choose'): the clean landing screen, just the two CTAs and a
  // "How to play" link. Steps 2/3 ('create'/'join') collect identity first.
  if (step === 'choose') {
    return (
      <section class="room-lobby" data-testid="room-lobby" data-room-state={ws.status} data-step="choose">
        {/* B2-13 + W5: the home step shows a room error only when it
            answers the user's own join or create (status 'error'). A
            stale rejection racing a leave never lands here: the reducer
            ignores rejections while idle, and the WS service forgets the
            room code on leave/kick so no rejoin is replayed. */}
        {ws.status === 'error' && ws.error && (
          <div class="room-error" role="alert" data-testid="room-error">
            {ws.error.message}
          </div>
        )}
        <div class="room-lobby-actions">
          <button
            type="button"
            class="room-cta create"
            data-testid="room-create-start"
            onClick={() => goStep('create')}
          >
            Create Room
          </button>
          <button
            type="button"
            class="room-cta join"
            data-testid="room-join-start"
            onClick={() => goStep('join')}
          >
            Join Room
          </button>
        </div>
        {/* #4: a plainly-visible "How to play" link below the buttons, not
            the easy-to-miss "?" affordance. */}
        <HowToPlayTrigger variant="link" class="room-how-to-play" />
        {/* B3b F7: after a closed tab, a way back into the recent room.
            Joins through ws.joinRoom, the same path as the ?room= link. */}
        {lastRoomCode && (
          <button
            type="button"
            class="room-cta join room-rejoin"
            data-testid="room-rejoin"
            onClick={() => ws.joinRoom(lastRoomCode)}
          >
            {`Rejoin your game in room ${lastRoomCode}`}
          </button>
        )}
      </section>
    );
  }

  // Identity step (shared by 'create' and 'join'): "Your identity" panel,
  // a Back link, then the step-specific action (Create confirm / code entry).
  return (
    <section class="room-lobby" data-testid="room-lobby" data-room-state={ws.status} data-step={step}>
      <button
        type="button"
        class="room-back"
        data-testid="lobby-back"
        onClick={() => goStep('choose')}
      >
        ← Back
      </button>

      {stepShowsIdentity(step) && <IdentityPanel onChange={handleIdentityChange} />}

      {step === 'create' && (
        <div class="room-lobby-actions">
          <button
            type="button"
            class="room-cta create"
            data-testid="room-create"
            onClick={handleCreate}
            disabled={nameMissing || ws.status === 'creating'}
            aria-disabled={nameMissing || ws.status === 'creating'}
            title={nameMissing ? 'Set your name first' : 'Create a new room'}
          >
            {ws.status === 'creating' ? 'Creating…' : 'Create Room'}
          </button>
        </div>
      )}

      {/* #4 (legacy): surface create-flow rejections instead of failing silently. */}
      {step === 'create' && ws.error && (
        <div class="room-create-error" role="alert" data-testid="room-create-error">
          {ws.error.message}
        </div>
      )}

      {step === 'join' && (
        <form class="room-join-form" onSubmit={handleJoinSubmit} data-testid="room-join-form">
          <label class="room-join-label" for="room-join-input">Room code</label>
          <input
            id="room-join-input"
            class="room-join-input"
            data-testid="room-join-input"
            type="text"
            value={codeDraft}
            maxLength={ROOM_CODE_LENGTH}
            autoComplete="off"
            autoCapitalize="characters"
            spellcheck={false}
            inputMode="text"
            placeholder="ABC123"
            onInput={handleCodeInput}
            aria-describedby={ws.error ? 'room-join-error' : undefined}
            aria-invalid={ws.error ? 'true' : 'false'}
          />
          {/* U6: the error sits directly under the code box (above the
              full-width Join button), so it reads as answering the code
              entry, matching the Create Room main-button pattern. */}
          {ws.error && (
            <div
              id="room-join-error"
              class="room-join-error"
              role="alert"
              data-testid="room-join-error"
            >
              {ws.error.message}
            </div>
          )}
          <button
            type="submit"
            class="room-join-submit"
            data-testid="room-join-submit"
            disabled={codeDraft.length !== ROOM_CODE_LENGTH || ws.status === 'joining'}
          >
            {ws.status === 'joining' ? 'Joining…' : 'Join'}
          </button>
        </form>
      )}
    </section>
  );
}
